import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, Clock, Database, Download, Eye, Search, Server, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { STATUSES } from '../../components/backup/BackupFields';
import { useAuth } from '../../context/AuthContext';

const STATUS_LABELS = { sin_declaracion: 'Sin declaración', ...STATUSES };
const STATUS_CLASSES = { sin_declaracion: 'badge-default', protegido: 'badge-success', pendiente: 'badge-warning', excepcion: 'badge-gray' };
const EMPTY_FILTERS = { site_id: '', policy_id: '', tool: '', status: '' };
const PAGE_SIZE = 25;

const SummaryCard = ({ label, value, icon: Icon, selected, onClick, disabled, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} aria-pressed={selected}
    className={`stats-card flex items-center gap-3 text-left transition-all ${selected ? 'ring-2 ring-primary' : 'hover:ring-2 hover:ring-primary/40'} disabled:opacity-60`}>
    <Icon className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
    <span><span className="stats-card-value block">{value ?? '—'}</span><span className="stats-card-label block">{label}</span></span>
  </button>
);

const SortHeader = ({ field, label, sort, onSort }) => {
  const selected = sort.field === field;
  const Icon = !selected ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th scope="col" aria-sort={selected ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="flex items-center gap-1 hover:text-primary" onClick={() => onSort(field)}>
        {label}<Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
    </th>
  );
};

const BackupCoverageReport = () => {
  const { canRead } = useAuth();
  const allowed = ['reports.backup_coverage', 'assets.servers', 'assets.server_backups'].every(canRead);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ field: 'hostname', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [options, setOptions] = useState({ sites: [], policies: [], tools: [] });
  const [optionsError, setOptionsError] = useState(false);
  const [optionsRetry, setOptionsRetry] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (searchInput.trim() === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const baseQuery = useMemo(() => {
    const params = new URLSearchParams({ sort_by: sort.field, sort_dir: sort.dir });
    if (search) params.set('search', search);
    Object.entries(filters).forEach(([key, value]) => { if (value !== '') params.set(key, value); });
    return params.toString();
  }, [search, filters, sort]);

  useEffect(() => {
    if (!allowed) return undefined;
    const controller = new AbortController();
    setOptionsError(false);
    api.get('/reports/backup-coverage/options', { signal: controller.signal })
      .then(({ data: result }) => { if (!controller.signal.aborted) setOptions(result); })
      .catch(() => { if (!controller.signal.aborted) setOptionsError(true); });
    return () => controller.abort();
  }, [allowed, optionsRetry]);

  useEffect(() => {
    if (!allowed) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setData(null);
    api.get(`/reports/backup-coverage?${baseQuery}&page=${page}&page_size=${PAGE_SIZE}`, { signal: controller.signal })
      .then(({ data: result }) => {
        if (controller.signal.aborted) return;
        if (page > result.pages) { setPage(result.pages); return; }
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) { setError(true); setLoading(false); }
      });
    return () => controller.abort();
  }, [allowed, baseQuery, page, retry]);

  const updateFilter = (key, value) => {
    setFilters(previous => ({ ...previous, [key]: value }));
    setPage(1);
  };
  const handleSort = field => {
    setSort(previous => ({ field, dir: previous.field === field && previous.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };
  const clearFilters = () => {
    setSearchInput(''); setSearch(''); setFilters(EMPTY_FILTERS); setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/reports/backup-coverage/export?${baseQuery}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cobertura_backups.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch {
      toast.error('No se pudo exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  if (!allowed) {
    return <Layout><Card className="card-subtle p-8 text-center"><p className="text-muted-foreground">No tiene permisos para consultar la cobertura de backups.</p></Card></Layout>;
  }

  const summary = data?.summary;
  const pendingSearch = searchInput.trim() !== search;
  const anyFilter = searchInput || Object.values(filters).some(Boolean);
  const cards = [
    { key: '', label: 'Servidores', value: summary?.servers, Icon: Server, testid: 'coverage-total' },
    { key: 'sin_declaracion', label: 'Sin declaración', value: summary?.without_declaration, Icon: Database, testid: 'coverage-none' },
    { key: 'protegido', label: 'Declarado protegido', value: summary?.declared_protected, Icon: CheckCircle2, testid: 'coverage-protected' },
    { key: 'pendiente', label: 'Con pendiente', value: summary?.pending, Icon: Clock, testid: 'coverage-pending' },
    { key: 'excepcion', label: 'Con excepción', value: summary?.exceptions, Icon: AlertTriangle, testid: 'coverage-exception' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h1 className="page-title">Cobertura de backups</h1><p className="page-subtitle">Protecciones declaradas para los servidores del inventario</p></div>
          <Button variant="outline" onClick={handleExport} disabled={exporting || loading || error || pendingSearch} data-testid="coverage-export" title="Exporta todas las filas filtradas">
            <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />{exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          Inventario manual: las declaraciones no verifican la ejecución del backup ni su recuperación.
        </div>

        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="coverage-summary">
            {cards.map(card => <SummaryCard key={card.testid} label={card.label} value={card.value} icon={card.Icon} testid={card.testid}
              selected={filters.status === card.key} disabled={loading || error} onClick={() => updateFilter('status', filters.status === card.key ? '' : card.key)} />)}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Contadores por servidor según búsqueda, site, política y herramienta. Un servidor puede tener varios estados.</p>
        </div>

        <Card className="card-subtle"><CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" strokeWidth={1.5} /><Input aria-label="Buscar en cobertura" placeholder="Servidor, IP, objeto, job o política..." maxLength={150} value={searchInput} onChange={event => setSearchInput(event.target.value)} className="pl-9 h-9" data-testid="coverage-search" /></div>
            <Select value={filters.site_id || 'all'} onValueChange={value => updateFilter('site_id', value === 'all' ? '' : value)}>
              <SelectTrigger className="h-9" aria-label="Filtrar por site"><SelectValue placeholder="Todos los sites" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los sites</SelectItem><SelectItem value="0">Sin site</SelectItem>{options.sites.map(item => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.policy_id || 'all'} onValueChange={value => updateFilter('policy_id', value === 'all' ? '' : value)}>
              <SelectTrigger className="h-9" aria-label="Filtrar por política"><SelectValue placeholder="Todas las políticas" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas las políticas</SelectItem>{options.policies.map(item => <SelectItem key={item.id} value={String(item.id)}>{item.name}{!item.is_active ? ' (inactiva)' : ''}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.tool ? `tool:${filters.tool}` : 'all'} onValueChange={value => updateFilter('tool', value === 'all' ? '' : value.slice(5))}>
              <SelectTrigger className="h-9" aria-label="Filtrar por herramienta"><SelectValue placeholder="Todas las herramientas" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas las herramientas</SelectItem>{options.tools.map(item => <SelectItem key={item} value={`tool:${item}`}>{item}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={filters.status || 'all'} onValueChange={value => updateFilter('status', value === 'all' ? '' : value)}>
              <SelectTrigger className="h-9 w-full sm:w-56" aria-label="Filtrar por estado declarado"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los estados</SelectItem>{Object.entries(STATUS_LABELS).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent>
            </Select>
            {anyFilter && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" strokeWidth={1.5} />Limpiar filtros</Button>}
          </div>
          {optionsError && <div role="alert" className="text-sm text-muted-foreground">No se pudieron cargar las opciones de filtros. <button type="button" className="text-primary underline" onClick={() => setOptionsRetry(value => value + 1)}>Reintentar</button></div>}
        </CardContent></Card>

        <Card className="card-subtle overflow-hidden"><CardContent className="p-0">
          {error ? <div role="alert" className="p-8 text-center text-sm text-muted-foreground">No se pudo cargar el reporte. <button type="button" className="text-primary underline" onClick={() => setRetry(value => value + 1)}>Reintentar</button></div>
            : loading ? <div className="p-12 text-center text-sm text-muted-foreground" role="status">Cargando reporte...</div>
              : data?.items.length === 0 ? <div className="p-12 text-center text-sm text-muted-foreground">No hay resultados para los filtros seleccionados.</div>
                : <div className="overflow-x-auto"><table className="table-subtle" data-testid="coverage-table"><thead><tr>
                  <SortHeader field="hostname" label="SERVIDOR" sort={sort} onSort={handleSort} /><th>IP</th>
                  <SortHeader field="site" label="SITE" sort={sort} onSort={handleSort} />
                  <SortHeader field="protected_object" label="QUÉ SE PROTEGE" sort={sort} onSort={handleSort} />
                  <SortHeader field="policy" label="POLÍTICA" sort={sort} onSort={handleSort} />
                  <SortHeader field="tool" label="HERRAMIENTA" sort={sort} onSort={handleSort} />
                  <th>JOB / GRUPO</th><SortHeader field="status" label="ESTADO DECLARADO" sort={sort} onSort={handleSort} />
                  <th>MOTIVO DE EXCEPCIÓN</th><th className="text-right">ACCIÓN</th>
                </tr></thead><tbody>{data.items.map(item => {
                  const target = `/assets/servers/${encodeURIComponent(item.hostname)}?tab=backups`;
                  return <tr key={`${item.server_id}-${item.assignment_id ?? 'none'}`}>
                    <td><Link className="font-medium text-primary hover:underline" to={target}>{item.hostname}</Link></td>
                    <td className="font-mono text-sm text-muted-foreground">{item.ip || '—'}</td><td>{item.site}</td>
                    <td>{item.protected_object || '—'}</td>
                    <td>{item.policy ? <>{item.policy}{item.policy_active === false && <span className="text-xs text-muted-foreground"> (inactiva)</span>}</> : item.assignment_id ? 'Política no disponible' : '—'}</td>
                    <td>{item.tool || '—'}</td><td>{item.job_name || '—'}</td>
                    <td><span className={`badge ${STATUS_CLASSES[item.status] || 'badge-default'}`}>{STATUS_LABELS[item.status] || 'Estado no reconocido'}</span></td>
                    <td className="max-w-[260px] whitespace-normal">{item.exception_reason || '—'}</td>
                    <td className="text-right"><Link to={target} className="action-btn inline-flex" aria-label={`Ver backups de ${item.hostname}`} title="Ver backups"><Eye className="h-3.5 w-3.5" strokeWidth={1.5} /></Link></td>
                  </tr>;
                })}</tbody></table></div>}
          {!loading && !error && data && <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm text-muted-foreground">
            <span>{data.total === 0 ? 0 : (data.page - 1) * PAGE_SIZE + 1}–{Math.min(data.page * PAGE_SIZE, data.total)} de {data.total} filas</span>
            <div className="flex items-center gap-2"><Button variant="outline" size="sm" aria-label="Página anterior" disabled={page <= 1} onClick={() => setPage(value => value - 1)}><ChevronLeft className="h-4 w-4" strokeWidth={1.5} /></Button><span>{data.page} / {data.pages}</span><Button variant="outline" size="sm" aria-label="Página siguiente" disabled={page >= data.pages} onClick={() => setPage(value => value + 1)}><ChevronRight className="h-4 w-4" strokeWidth={1.5} /></Button></div>
          </div>}
        </CardContent></Card>
        <p className="text-xs text-muted-foreground">Se muestra una fila por protección y una por servidor sin declaraciones. La exportación incluye todas las filas filtradas.</p>
      </div>
    </Layout>
  );
};

export default BackupCoverageReport;
